import { useEffect, type RefObject } from 'react'
import type { ChartManager } from '../core/ChartManager'
import { isIntradayPeriod, type Period } from '../utils/resolution'

/**
 * Whether the current-bar countdown should run at all.
 *
 * Intraday only. A minute or hourly bar closes on a rolling clock, so "3:45 to
 * close" is real information. A daily, weekly or monthly bar closes at the end
 * of the session, week or month, and a timer against that is noise on the price
 * scale rather than a trading signal (Chris, 2026-09-10). The user's own
 * `hideBarCountdown` setting still wins where it is set; this only removes the
 * timeframes where the timer never made sense.
 */
export function barCountdownEnabled({
  period,
  hideBarCountdown,
  hideLastPriceLabel,
  historical,
}: {
  period: Period
  hideBarCountdown: boolean
  hideLastPriceLabel: boolean
  /** Anchored/historical charts have no forming bar to count down to. */
  historical: boolean
}): boolean {
  if (hideBarCountdown || hideLastPriceLabel || historical) return false
  return isIntradayPeriod(period)
}

export { formatCountdown } from '../utils/barCountdownPill'

/**
 * Runs the canvas countdown pill (utils/barCountdownPill.ts) while `enabled`.
 * No React state: the text is read when klinecharts paints, so EdgeChart no
 * longer re-renders every second, and the crosshair price label, which
 * klinecharts paints after every axis figure, stays on top of it.
 */
export function useBarCountdown(
  managerRef: RefObject<ChartManager | null>,
  chartInstance: unknown,
  enabled: boolean,
): void {
  useEffect(() => {
    const manager = managerRef.current
    if (!enabled || !manager || !chartInstance) return
    manager.setBarCountdownVisible(true)
    const id = setInterval(() => manager.tickBarCountdown(Date.now()), 1000)
    return () => {
      clearInterval(id)
      manager.setBarCountdownVisible(false)
    }
  }, [managerRef, chartInstance, enabled])
}
